import React, { useState, useEffect, useMemo } from 'react';
import { Intervention } from '../../types';

interface TechnicentreValidationModalProps {
  show: boolean;
  intervention: Intervention;
  onSave: (intervention: Partial<Intervention>) => void;
  onClose: () => void;
  onContinue: (selectedEngines: { name: string; index: number }[], rejectedEngines: { engine: string; index: number; reason: string }[]) => void;
}

interface EnginItem {
  id: string;
  numero: string;
  position: number;
}

// ===== Composant TechnicentreValidationModal =====
const TechnicentreValidationModal: React.FC<TechnicentreValidationModalProps> = ({
  show,
  intervention,
  onSave,
  onClose,
  onContinue,
}) => {

  const [validationData, setValidationData] = useState<Record<string, string>>({});
  const [commentsData, setCommentsData] = useState<Record<string, string>>({});
  const [globalRefusalReason, setGlobalRefusalReason] = useState('');
  const [isFormComplete, setIsFormComplete] = useState(false);

  const enginItems: EnginItem[] = useMemo(() => {
    const items: EnginItem[] = [];

    if (intervention?.engin_1) {
      items.push({ id: 'engin_1', numero: intervention.engin_1, position: 0 });
    }
    if (intervention?.engin_2) {
      items.push({ id: 'engin_2', numero: intervention.engin_2, position: 1 });
    }
    if (intervention?.engin_3) {
      items.push({ id: 'engin_3', numero: intervention.engin_3, position: 2 });
    }
    if (intervention?.engin_4) {
      items.push({ id: 'engin_4', numero: intervention.engin_4, position: 3 });
    }

    return items;
  }, [intervention]);

  useEffect(() => {
    if (intervention && show) {

      let existingValidationData: Record<string, string> = {};
      let existingCommentsData: Record<string, string> = {};

      try {
        if (intervention.validation_donnees) {
          existingValidationData = JSON.parse(intervention.validation_donnees);
        }
      } catch (e) {
        console.error('Erreur parsing validation_donnees:', e);
      }

      try {
        if (intervention.commentaires_validation_donnees) {
          existingCommentsData = JSON.parse(intervention.commentaires_validation_donnees);
        }
      } catch (e) {
        console.error('Erreur parsing commentaires_validation_donnees:', e);
      }

      const initialValidation: Record<string, string> = {};
      const initialComments: Record<string, string> = {};

      enginItems.forEach((engin) => {
        initialValidation[engin.id] = existingValidationData[engin.id] || 'pending';
        initialComments[engin.id] = existingCommentsData[engin.id] || '';
      });

      setValidationData(initialValidation);
      setCommentsData(initialComments);
      setGlobalRefusalReason('');
    }
  }, [intervention, show, enginItems]);

  useEffect(() => {
    checkFormCompletion();
  }, [validationData, commentsData, globalRefusalReason]);

  const checkFormCompletion = () => {
    if (enginItems.length === 0) {
      setIsFormComplete(false);
      return;
    }

    const allHaveStatus = enginItems.every(engin =>
      validationData[engin.id] === 'done' || validationData[engin.id] === 'not_feasible'
    );

    if (!allHaveStatus) {
      setIsFormComplete(false);
      return;
    }

    const allRefused = enginItems.every(engin => validationData[engin.id] === 'not_feasible');
    if (allRefused) {
      setIsFormComplete(globalRefusalReason.trim().length > 0);
      return;
    }

    const refusedWithoutReason = enginItems.some(engin =>
      validationData[engin.id] === 'not_feasible' && !commentsData[engin.id]?.trim()
    );

    setIsFormComplete(!refusedWithoutReason);
  };

  const updateEnginStatus = (enginId: string, status: 'done' | 'not_feasible') => {
    setValidationData(prev => ({
      ...prev,
      [enginId]: status
    }));

    if (status === 'done') {
      setCommentsData(prev => ({
        ...prev,
        [enginId]: ''
      }));
    }
  };

  const updateEnginReason = (enginId: string, reason: string) => {
    setCommentsData(prev => ({
      ...prev,
      [enginId]: reason
    }));
  };

  const applyStatusToAllEngines = (status: 'done' | 'not_feasible') => {
    const nextValidationData: Record<string, string> = {};
    const nextCommentsData: Record<string, string> = {};

    enginItems.forEach((engin) => {
      nextValidationData[engin.id] = status;
      nextCommentsData[engin.id] = status === 'done' ? '' : (commentsData[engin.id] || '');
    });

    setValidationData(nextValidationData);
    setCommentsData(nextCommentsData);

    if (status === 'done') {
      setGlobalRefusalReason('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormComplete) {
      return;
    }

    const allRefused = enginItems.every(engin => validationData[engin.id] === 'not_feasible');

    if (allRefused && globalRefusalReason.trim()) {
      try {

        const formData: any = {
          id: intervention.id,
          motif_refus_global: globalRefusalReason
        };

        enginItems.forEach((engin) => {
          formData[`reponse-${engin.position}`] = 'non';
          if (commentsData[engin.id]) {
            formData[`raison-${engin.position}`] = commentsData[engin.id];
          }
        });

        console.log('=== TechnicentreValidationModal - Refus global ===');
        console.log('formData à envoyer:', formData);

        const response = await fetch('/api/interventions/validate-technicentre', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Erreur lors de la validation');
        }

        alert('✅ ' + result.message);
        onClose();
        window.location.reload();

      } catch (error: any) {
        console.error('Erreur lors de la validation:', error);
        alert('❌ Erreur lors de la validation: ' + error.message);
      }
      return;
    }

    const selectedEngines: { name: string; index: number }[] = [];
    const rejectedEngines: { engine: string; index: number; reason: string }[] = [];

    enginItems.forEach((engin) => {
      if (validationData[engin.id] === 'done') {
        selectedEngines.push({ name: engin.numero, index: engin.position });
      } else if (validationData[engin.id] === 'not_feasible') {
        rejectedEngines.push({
          engine: engin.numero,
          index: engin.position,
          reason: commentsData[engin.id] || ''
        });
      }
    });

    console.log('=== TechnicentreValidationModal - onContinue ===');
    console.log('validationData:', validationData);
    console.log('commentsData:', commentsData);
    console.log('selectedEngines:', selectedEngines);
    console.log('rejectedEngines:', rejectedEngines);

    onContinue(selectedEngines, rejectedEngines);
  };

  if (!show) return null;

  const allRefused = enginItems.length > 0 && enginItems.every(engin => validationData[engin.id] === 'not_feasible');

  const formTitle = allRefused ? 'Refus de l\'intervention' : 'Validation des engins';
  const titleColor = allRefused ? '#ef4444' : '#10b981';
  const submitText = allRefused ? 'Confirmer le refus' : 'Continuer la validation';
  const submitBgColor = allRefused ? '#ef4444' : '#3b82f6';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          width: '95%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: titleColor, margin: 0 }}>
            {formTitle}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            ×
          </button>
        </div>

        {/* Informations de l'intervention */}
        <div style={{ padding: '1rem 1.5rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
            <div><strong>Campagne:</strong> {intervention.campagne_nom || '-'}</div>
            <div><strong>Contact:</strong> {intervention.contact || '-'}</div>
            <div><strong>N° ELAN:</strong> {intervention.numero_elan || '-'}</div>
            <div><strong>Lieu:</strong> {intervention.site_nom || '-'}</div>
            <div><strong>Dispo:</strong> {formatDate(intervention.disponibilite_a_partir)}</div>
            <div><strong>Départ:</strong> {formatDate(intervention.depart_pour_mission)}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => applyStatusToAllEngines('done')}
              style={{
                flex: 1,
                padding: '0.65rem 1rem',
                backgroundColor: '#ecfdf5',
                color: '#047857',
                border: '2px solid #10b981',
                borderRadius: '0.5rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Accepter tous les engins
            </button>
            <button
              type="button"
              onClick={() => applyStatusToAllEngines('not_feasible')}
              style={{
                flex: 1,
                padding: '0.65rem 1rem',
                backgroundColor: '#fef2f2',
                color: '#b91c1c',
                border: '2px solid #ef4444',
                borderRadius: '0.5rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Refuser tous les engins
            </button>
          </div>

          {/* Liste des engins - UTILISE EXACTEMENT LA MÊME LOGIQUE QUE EnginStatusManager */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>
              Statut de validation pour chaque engin :
            </h3>

            {enginItems.map((engin) => {
              const status = validationData[engin.id] || 'pending';
              const reason = commentsData[engin.id] || '';

              const getStatusColor = () => {
                if (status === 'done') return '#10b981';
                if (status === 'not_feasible') return '#ef4444';
                return '#f59e0b';
              };

              return (
                <div
                  key={engin.id}
                  style={{
                    backgroundColor: '#ffffff',
                    border: `2px solid ${getStatusColor()}`,
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  {/* En-tête avec numéro d'engin */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#1f2937' }}>
                      {engin.numero}
                    </span>
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        backgroundColor: getStatusColor(),
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {status === 'done' ? 'Validé' : status === 'not_feasible' ? 'Refusé' : 'En attente'}
                    </span>
                  </div>

                  {/* Boutons ACCEPTER / REFUSER */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: status === 'not_feasible' && !allRefused ? '0.75rem' : 0 }}>
                    <button
                      type="button"
                      onClick={() => updateEnginStatus(engin.id, 'done')}
                      style={{
                        flex: 1,
                        padding: '0.5rem 1rem',
                        backgroundColor: status === 'done' ? '#10b981' : '#ffffff',
                        color: status === 'done' ? '#ffffff' : '#10b981',
                        border: '2px solid #10b981',
                        borderRadius: '0.375rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      ACCEPTER
                    </button>
                    <button
                      type="button"
                      onClick={() => updateEnginStatus(engin.id, 'not_feasible')}
                      style={{
                        flex: 1,
                        padding: '0.5rem 1rem',
                        backgroundColor: status === 'not_feasible' ? '#ef4444' : '#ffffff',
                        color: status === 'not_feasible' ? '#ffffff' : '#ef4444',
                        border: '2px solid #ef4444',
                        borderRadius: '0.375rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      REFUSER
                    </button>
                  </div>

                  {/* Raison du refus (si refusé ET pas tous refusés) */}
                  {status === 'not_feasible' && !allRefused && (
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => updateEnginReason(engin.id, e.target.value)}
                      placeholder="Raison du refus de cet engin..."
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Raison globale si tous refusés */}
          {allRefused && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#374151' }}>
                Raison globale du refus :
              </label>
              <textarea
                value={globalRefusalReason}
                onChange={(e) => setGlobalRefusalReason(e.target.value)}
                placeholder="Expliquez pourquoi tous les engins sont refusés..."
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  minHeight: '80px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
            </div>
          )}

          {/* Boutons d'action */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isFormComplete}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: isFormComplete ? submitBgColor : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: 600,
                cursor: isFormComplete ? 'pointer' : 'not-allowed',
                opacity: isFormComplete ? 1 : 0.7,
              }}
            >
              {submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TechnicentreValidationModal;
